export const UCAT_CALCULATOR_LABELS = {
  onClear: 'ON/C',
  memoryPlus: 'M+',
  memoryMinus: 'M−',
  memoryRecall: 'MRC',
  percent: '%',
  sqrt: '√',
  divide: '÷',
  multiply: '×',
  minus: '−',
  plus: '+',
  equals: '=',
  decimal: '.',
  negate: '+/−',
};

// TI-108 shows 8 significant digits on the display.
const DISPLAY_DIGITS = 8;

export function createCalculatorState() {
  return {
    display: '0',
    acc: null,
    op: null,
    memory: 0,
    waiting: false,
    error: false,
    // Set for exactly one step after ON/C clears the entry (stage 1), so the
    // very next ON/C press clears everything instead (stage 2). Any other
    // key press resets this, so it never lingers across unrelated input.
    justClearedEntry: false,
    // Set for exactly one step after MRC recalls memory to the display, so
    // the very next MRC press clears memory instead of re-recalling it.
    justRecalledMemory: false,
  };
}

function formatNumber(value) {
  if (!Number.isFinite(value)) return 'Error';
  const rounded = Number(value.toPrecision(DISPLAY_DIGITS));
  const asString = String(rounded);
  if (asString.replace('-', '').replace('.', '').length <= DISPLAY_DIGITS) return asString;
  return rounded.toExponential(DISPLAY_DIGITS - 1).replace(/\.?0+e/, 'e');
}

function applyOp(left, right, op) {
  if (op === '+') return left + right;
  if (op === '-') return left - right;
  if (op === '*') return left * right;
  if (op === '/') return right === 0 ? NaN : left / right;
  return right;
}

export function applyCalculatorInput(state, key) {
  if (key === 'ON/C') {
    // Stage 2 (also covers recovering from Error, and pressing ON/C when
    // there is nothing pending to clear): an immediately-preceding ON/C
    // already cleared the entry, so this press resets everything except
    // memory — MRC-MRC is the only way to clear memory.
    if (state.display === 'Error' || state.justClearedEntry
      || (state.op == null && state.acc == null && state.display === '0')) {
      return { ...createCalculatorState(), memory: state.memory };
    }
    // Stage 1: clear the current entry only, leaving any pending operation
    // and accumulator in place.
    return {
      ...state,
      display: '0',
      error: false,
      waiting: false,
      justClearedEntry: true,
      justRecalledMemory: false,
    };
  }

  // Error state is a dead end: nothing but ON/C recovers from it.
  if (state.display === 'Error') return state;

  const next = { ...state, error: false, justClearedEntry: false };

  if (key === 'M+') {
    next.memory += Number(next.display) || 0;
    next.waiting = true;
    return next;
  }
  if (key === 'M-') {
    next.memory -= Number(next.display) || 0;
    next.waiting = true;
    return next;
  }
  if (key === 'MRC') {
    // First press recalls memory to the display; a second consecutive press
    // clears memory.
    if (state.justRecalledMemory) {
      next.memory = 0;
      next.display = formatNumber(0);
      next.justRecalledMemory = false;
      next.waiting = true;
      return next;
    }
    next.display = formatNumber(next.memory);
    next.waiting = true;
    next.justRecalledMemory = true;
    return next;
  }
  next.justRecalledMemory = false;

  if (key === '+/-') {
    next.display = formatNumber(-(Number(next.display) || 0));
    return next;
  }

  if (key === 'sqrt') {
    const value = Math.sqrt(Number(next.display));
    next.display = formatNumber(value);
    if (next.display === 'Error') next.error = true;
    next.waiting = true;
    return next;
  }

  if (key === '%') {
    // `+`/`-`: `50 + 10 %` → 55 (10% of the accumulator, applied with the
    // pending op). `*`/`/`: `50 × 10 %` → 5 (current as a percentage operand).
    if (next.op && next.acc != null) {
      const current = Number(next.display);
      const percentValue = (next.op === '*' || next.op === '/')
        ? current / 100
        : (next.acc * current) / 100;
      const result = applyOp(next.acc, percentValue, next.op);
      next.display = formatNumber(result);
      next.acc = null;
      next.op = null;
      next.waiting = true;
      return next;
    }
    next.display = formatNumber(Number(next.display) / 100);
    next.waiting = true;
    return next;
  }

  if (key === '.') {
    if (next.waiting) {
      next.display = '0.';
      next.waiting = false;
      return next;
    }
    if (!next.display.includes('.')) next.display = `${next.display}.`;
    return next;
  }

  if (/^[0-9]$/.test(key)) {
    if (next.waiting || next.display === '0') {
      next.display = key;
      next.waiting = false;
      return next;
    }
    const digitCount = next.display.replace('-', '').replace('.', '').length;
    if (digitCount >= DISPLAY_DIGITS) return next;
    next.display = `${next.display}${key}`;
    return next;
  }

  if (key === '=') {
    if (next.op && next.acc != null) {
      const result = applyOp(next.acc, Number(next.display), next.op);
      next.display = formatNumber(result);
      if (next.display === 'Error') next.error = true;
      next.acc = null;
      next.op = null;
      next.waiting = true;
    }
    return next;
  }

  if (['+', '-', '*', '/'].includes(key)) {
    const current = Number(next.display);
    if (next.op && next.acc != null && !next.waiting) {
      const result = applyOp(next.acc, current, next.op);
      next.display = formatNumber(result);
      if (next.display === 'Error') {
        next.error = true;
        next.acc = null;
        next.op = null;
        next.waiting = true;
        return next;
      }
      next.acc = result;
    } else {
      next.acc = current;
    }
    next.op = key;
    next.waiting = true;
    return next;
  }

  return next;
}
